// Choosing what to upload when someone picks a whole photo folder.
//
// After "Save Metadata to File", the .xmp and .acr sit next to their raws,
// scattered across however many folders the photographer organised by. Asking
// them to gather those by hand is asking for mistakes, so the browser hands
// over everything underneath the chosen folder and this decides what actually
// goes.
//
// Two things it must get right:
//
//   - Raws are NOT uploaded. A 4000-photo catalogue is ~259 GB of raws against
//     ~315 MB of edits, and when the raws are already in the library the edits
//     alone are all that is missing.
//   - The folder structure is KEPT. A camera counter rolls at 9999, so two
//     different photographs really can both be DSC09679; flattening means the
//     second sidecar overwrites the first before the server ever sees it.
//   - Each import gets its own PREFIX. Somebody with photos on an external
//     drive, on their Mac and in iCloud picks three folders, and those are
//     routinely all called "Lightroom" -- without a prefix the second import
//     lands on top of the first. The prefix is scaffolding only: the server
//     files each sidecar next to its photo and prunes the inbox folders, so
//     nothing of it survives ingest.

const EDIT_EXT = /\.(xmp|acr)$/i;

// A label unique to this import, safe as a folder name.
function defaultBatchLabel(sample) {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  const stamp = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}` +
                `-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
  const top = String(sample || "").split("/")[0].replace(/[^A-Za-z0-9._-]+/g, "-").slice(0, 40);
  return top ? `${top}-${stamp}` : `import-${stamp}`;
}

export function pickEdits(fileList, opts = {}) {
  const all = Array.from(fileList || []);
  const first = all.length ? (all[0].webkitRelativePath || all[0].name || "") : "";
  const batch = opts.batchLabel || defaultBatchLabel(first);
  const edits = [];
  const folders = new Set();
  let bytes = 0;

  for (const f of all) {
    if (!EDIT_EXT.test(f.name || "")) continue;

    // webkitRelativePath is "<chosen folder>/2025/Oct/DSC09679.xmp". Dropping
    // the filename leaves the subfolder, and keeping the chosen folder's own
    // name means two catalogues imported separately cannot collide.
    const relPath = f.webkitRelativePath || f.name || "";
    const parts = relPath.split("/");
    parts.pop();
    const subdir = [batch, ...parts.filter(Boolean)].join("/");
    folders.add(subdir);

    // Files are plain File objects; tagging is how the uploader learns where
    // each belongs without changing its signature. A frozen File falls back to
    // the inbox root, which is still correct — just flat.
    try { f.__destSubdir = subdir; } catch (e) { /* ignore */ }

    edits.push(f);
    bytes += f.size || 0;
  }

  return { edits, skipped: all.length - edits.length, folders: folders.size, bytes, batch };
}
